<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <div class="w-full max-w-3xl m-3 overflow-hidden transition-all duration-200 bg-gray-800 rounded-lg shadow-xl">

      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-600">
        <div class="flex items-center gap-3">
          <img v-if="villagerData.normalFace" :src="villagerData.normalFace" class="object-cover w-12 h-12 rounded-md border-2 border-indigo-400" @error="handleImageError" />
          <div>
            <h1 class="text-lg font-semibold text-indigo-200">{{ villagerData.name }}</h1>
            <div v-if="villagerData.level" class="flex items-center gap-1 mt-1">
              <i v-for="i in villagerData.level" :key="i" class="text-xs fas fa-star text-yellow-400"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="p-4">
        <div class="flex border-b border-gray-600">
          <button @click="activeTab = 'info'"
            class="px-3 py-2 text-xs font-medium sm:text-sm"
            :class="activeTab === 'info' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'">
            ข้อมูล
          </button>
          <button @click="activeTab = 'locations'"
            class="px-3 py-2 text-xs font-medium sm:text-sm"
            :class="activeTab === 'locations' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'">
            สถานที่พบเจอ
          </button>
          <button @click="activeTab = 'upgrade'"
            class="px-3 py-2 text-xs font-medium sm:text-sm"
            :class="activeTab === 'upgrade' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'">
            อัปเกรด
          </button>
        </div>
      </div>

      <div class="px-4 py-4 space-y-4 overflow-y-auto max-h-96">

        <div v-if="isLoading" class="flex justify-center py-4">
          <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
        </div>

        <!-- INFO TAB -->
        <div v-else-if="activeTab === 'info'" class="space-y-4">

          <!-- Hire Cost -->
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-2">ค่าใช้จ่ายในการจ้าง</h3>
            <div class="grid grid-cols-3 gap-2 text-sm text-indigo-200">
              <div><i class="fas fa-coins"></i> เงิน: <strong>{{ villagerData.hireCosts?.silver || 0 }}</strong></div>
              <div><i class="fas fa-g text-yellow-400"></i> ทอง: <strong>{{ villagerData.hireCosts?.gold || 0 }}</strong></div>
              <div><i class="fas fa-gem text-blue-400"></i> เพชร: <strong>{{ villagerData.hireCosts?.diamond || 0 }}</strong></div>
            </div>
          </section>
     
          <!-- Stats (No Bar Version) -->
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-2">ค่าสถานะ</h3>
            <div class="grid grid-cols-3 gap-2 text-sm text-indigo-200">
              <div>Sta: <strong>{{ villagerData.stats?.stamina || 0 }}</strong></div>
              <div>Str: <strong>{{ villagerData.stats?.str || 0 }}</strong></div>
              <div>Int: <strong>{{ villagerData.stats?.int || 0 }}</strong></div>
              <div>Agi: <strong>{{ villagerData.stats?.agi || 0 }}</strong></div>
              <div>Def: <strong>{{ villagerData.stats?.defense || 0 }}</strong></div>
              <div>Luck: <strong>{{ villagerData.stats?.luck || 0 }}</strong></div>
            </div>
          </section>


          <!-- Dice -->
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-2">จำนวนลูกเต๋า</h3>
            <div class="grid grid-cols-2 gap-3 text-sm text-indigo-200">
              <div>ฝ่ายบุก: <strong>{{ villagerData.attackDice }}</strong></div>
              <div>ฝ่ายตั้งรับ: <strong>{{ villagerData.defenseDice }}</strong></div>
            </div>
          </section>

          <!-- Weapon -->
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-2">อาวุธ</h3>
            <div class="text-sm text-indigo-200">{{ villagerData.weapon || 'ไม่มีอาวุธ' }}</div>
          </section>

          <!-- Strong / Weak -->
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-3">อาวุธที่แพ้–ชนะ</h3>

            <p class="text-xs text-indigo-300 mb-1">อาวุธที่ชนะทาง</p>
            <div class="flex flex-wrap gap-2 mb-3">
              <span v-for="w in villagerData.strongAgainst || []" :key="'s'+w"
                class="px-2 py-1 text-xs rounded-full bg-green-700 border border-green-500 text-green-100">{{ w }}</span>
              <span v-if="!(villagerData.strongAgainst && villagerData.strongAgainst.length)" class="text-xs text-indigo-300">— ไม่มีข้อมูล —</span>
            </div>

            <p class="text-xs text-indigo-300 mb-1">อาวุธที่แพ้ทาง</p>
            <div class="flex flex-wrap gap-2">
              <span v-for="w in villagerData.weakAgainst || []" :key="'w'+w"
                class="px-2 py-1 text-xs rounded-full bg-red-700 border border-red-500 text-red-100">{{ w }}</span>
              <span v-if="!(villagerData.weakAgainst && villagerData.weakAgainst.length)" class="text-xs text-indigo-300">— ไม่มีข้อมูล —</span>
            </div>
          </section>

          <!-- Locations -->
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-3">สถานที่ที่สามารถส่งไปได้</h3>
            <div class="flex flex-wrap gap-2">
              <span v-for="loc in villagerData.locations || []" :key="loc"
                class="px-2 py-1 text-xs rounded-full bg-indigo-700 border border-indigo-500 text-indigo-100">{{ loc }}</span>
              <span v-if="!(villagerData.locations && villagerData.locations.length)" class="text-xs text-indigo-300">— ไม่มีข้อมูล —</span>
            </div>
          </section>

          <!-- Perks -->
          <section v-if="villagerData.perks?.length" class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-2">Perks</h3>
            <ul class="space-y-2 text-sm text-indigo-200">
              <li v-for="p in villagerData.perks" :key="p">
                <strong>{{ p }}</strong> - {{ getPerkDescription(p) }}
              </li>
            </ul>
          </section>

        </div>

        <!-- LOCATIONS TAB -->
        <div v-else-if="activeTab === 'locations'">
          <div v-if="loadingLocations" class="flex justify-center py-4">
            <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
          </div>
          <div v-else-if="!locations.length" class="text-xs text-center text-indigo-200">ไม่มีข้อมูล</div>
          <div v-else class="space-y-3">
            <div v-for="m in locations" :key="m.id" class="p-3 bg-gray-700 rounded-md border border-gray-600">
              <div class="flex items-center gap-3">
                <div>
                  <h3 class="text-sm text-indigo-200 font-semibold">{{ m.name }}</h3>
                </div>
              </div>
              <p class="text-xs text-indigo-200  mt-2">โอกาสพบ: <strong>{{ (m.chance/100).toFixed(2) }}%</strong></p>
            </div>
          </div>
        </div>

        <!-- UPGRADE TAB -->
        <div v-else-if="activeTab === 'upgrade'" class="space-y-4">
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-3">ไอเทมที่สามารถใช้อัพเลเวลชาวบ้านได้</h3>
            <div v-if="loadingUpgrade" class="flex justify-center py-4">
              <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
            </div>
            <div v-else-if="!upgradeItems.length" class="text-xs text-indigo-300">ไม่มีไอเท็มสำหรับอัปเกรด</div>
            <div v-else class="space-y-3">
              <div v-for="(item, index) in upgradeItems" :key="index" class="flex items-center gap-3 p-2 bg-gray-600 rounded-md">
                <img :src="item.img" class="w-10 h-10 rounded border border-gray-500 object-cover" @error="handleImageError" />
                <div class="flex-1">
                  <span class="text-sm text-indigo-200 font-medium">{{ item.name }}</span>
                </div>
              </div>
            </div>
          </section>
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-3">ค่าอัปเกรดเลเวล (diamond และ gold สำหรับเลเวล 1-10)</h3>
            <div class="grid grid-cols-3 gap-4 font-medium text-indigo-200 text-sm">
              <div>เลเวล (อัปไปถัดไป)</div>
              <div>Diamond</div>
              <div>Gold</div>
            </div>
            <div v-for="lv in 9" :key="lv" class="grid grid-cols-3 gap-4 items-center text-indigo-200 text-sm mt-2">
              <div>{{ lv }} → {{ lv + 1 }}</div>
              <div>{{ villagerData.diamondCosts ? (villagerData.diamondCosts[lv-1] || 0) : 0 }}</div>
              <div>{{ villagerData.goldCosts ? (villagerData.goldCosts[lv-1] || 0) : 0 }}</div>
            </div>
          </section>
        </div>

      </div>

      <div class="flex justify-end p-4 border-t border-gray-600">
        <button @click="$emit('cancelModal')" class="px-4 py-1 bg-indigo-600 text-indigo-200 rounded hover:bg-indigo-700">ปิด</button>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import gameVillagersData from '@/data/gameVillagers.json';
import gameItemsData from '@/data/gameItems.json';

export default {
  props: ['villagerId'],
  emits: ['cancelModal'],
  setup(props) {
    const { user } = getUser();
    const villagerData = ref({});
    const isLoading = ref(true);
    const activeTab = ref('info');
    const locations = ref([]);
    const loadingLocations = ref(false);
    const upgradeItems = ref([]);
    const loadingUpgrade = ref(false);

    const availablePerks = [
      { name: 'ผู้รอดชีวิต', description: 'ถ้าถูกฆ่าตายมีโอกาสกลับไปที่แคมป์ (โอกาส 50%)' },
      { name: 'บ้าคลั่ง', description: 'เมื่อมีเลือดต่ำกว่า 50 % มีโอกาเพิ่มลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'นักวางกับดัก', description: 'เมื่ออยู่ฝ่ายป้องกันมีโอกาสเพิ่มลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'นักบุกโจมตี', description: 'เมื่ออยู่ฝ่ายโจมตีมีโอกาสเพิ่มลูกเต๋า 1 โอกาส (โอกาส 20%)' },
      { name: 'อึดถึกทน', description: 'ลดดาเมจที่ได้รับ 20% (โอกาส 30%)' },
      { name: 'ฟื้นตัวไว', description: 'รักษาเลือด 10% max HP หลังรอบ (โอกาส 10%)' },
      { name: 'หลอกล่อ', description: 'มีโอกาสทำให้อีกฝ่ายลดลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'ผู้มีบุญ', description: 'เมื่อ hp เหลือ 0 มีโอกาสที่จะไม่ตายและกลับมามีเลือด 20% (โอกาส 10%)' },
      { name: 'หัวหน้าทีม', description: 'Heal ทีม 10 % max HP หลังรอบ (โอกาส 10%)' },
      { name: 'ฆาตกรโรคจิต', description: 'มีโอกาส +2 dice ถ้าทีมเหลือ 2 คน (โอกาส 20%)' },
      { name: 'นักเจรจา', description: 'เมื่อทอยลูกเต๋าแพ้มีโอกาสที่จะไม่โดนโจมตีเลย (โอกาส 10%)' },
      { name: 'ผู้ใช้อาวุธระยะไกล', description: '+10% crit ถ้าแต้มลูกเต๋านำมากกว่า 3 แต้ม (โอกาส 100%)' }
    ];

    const getPerkDescription = (p) => availablePerks.find(x => x.name === p)?.description || '';

    const fetchVillager = async () => {
      villagerData.value = gameVillagersData[props.villagerId] || null;
      isLoading.value = false;
      await fetchUpgradeItems();
    };

    const fetchUpgradeItems = async () => {
      loadingUpgrade.value = true;
      if (villagerData.value?.upgradeItemNames?.length) {
        upgradeItems.value = villagerData.value.upgradeItemNames.map((itemName) => {
          const data = gameItemsData[itemName];
          return {
            name: itemName,
            img: data?.img || 'https://via.placeholder.com/64',
          };
        });
      }
      loadingUpgrade.value = false;
    };

    const fetchLocations = async () => {
      loadingLocations.value = true;
      const snap = await projectFirestore.collection('maps').get();
      const arr = [];
      snap.forEach(d => {
        const data = d.data();
        const match = (data.villagers || []).find(v => v.name === villagerData.value.name);
        if (match) arr.push({ id: d.id, name: data.name, imageUrl: data.image, requiredLevel: data.requiredLevel, chance: match.chance });
      });
      locations.value = arr.sort((a,b)=>b.chance - a.chance);
      loadingLocations.value = false;
    };

    const handleImageError = e => { e.target.src = 'https://via.placeholder.com/64'; };

    onMounted(async () => { await fetchVillager(); await fetchLocations(); });

    return { villagerData, isLoading, activeTab, locations, loadingLocations, getPerkDescription, handleImageError, upgradeItems, loadingUpgrade };
  }
};
</script>

<style scoped>
::-webkit-scrollbar { width: 5px; }
::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: #6b7280; }
</style>